import { Alert, LoadingOverlay } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getLogger } from 'loglevel';

import { ErrorIcon, Success, Warning } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { TitleLayout } from '@apple/ui/layouts/TitleLayout';
import { ServerValidationError } from '@apple/utils/api/errors';
import { useGlobalization } from '@apple/utils/globalization/hooks/useGlobalization';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { useAuthContext } from '@apple/features/auth-retail/hooks/useAuthContext';
import { AccountSettings } from '@apple/features/user-retail/components/AccountSettings';

const log = getLogger('profile');

export const Route = createFileRoute('/_authed/profile')({
	beforeLoad: args => requireAuth(args),
	component: ProfileRoute,
});

export function ProfileRoute() {
	const { t } = useTranslation('profile');
	const user = useAuthContext();
	const globalization = useGlobalization();

	function handleSuccessfulChange(message: string) {
		notifications.show({
			message: message,
			title: t('profile.save.success.title'),
			icon: <Success />,
			color: 'green',
			autoClose: 5000,
		});
	}

	function showErrorNotification(error: Error) {
		if (!(error instanceof ServerValidationError)) {
			log.error(error);
			notifications.show({
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
				color: 'red',
				autoClose: 5000,
			});
		}
	}
	const saveProfileMutation = useMutation({
		mutationFn: user.updateProfile,
		onSuccess: () => {
			handleSuccessfulChange(t('profile.save.success.message'));
		},
		onError: showErrorNotification,
	});

	if (user.profile === null || globalization.isLoading) {
		return <LoadingOverlay visible />;
	}

	return globalization.error ? (
		<Alert variant='light' color='red' title='Error' icon={<Warning />}>
			{t('loadingError')}
		</Alert>
	) : (
		<TitleLayout title={t('title')}>
			<AccountSettings
				profile={user.profile}
				updateProfile={saveProfileMutation.mutateAsync}
				changePassword={user.changePassword}
				timeZones={globalization.timeZones}
				countries={globalization.countries}
			/>
		</TitleLayout>
	);
}
